import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { searchAllCards, getSetCards, normalizeSetName, getSimilarCards } from '../services/pokemonTcgApi';
import { supabase } from '../lib/supabase';
import CardDetails from '../components/CardDetails';
import SimilarCards from '../components/SimilarCards';
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { transformCard } from '../utils/cardTransformer';
import { slugify } from '../utils/slugify';
import CardDetailsSkeleton from '../components/skeletons/CardDetailsSkeleton';
import Breadcrumbs from '../components/Breadcrumbs';
import LoadingHeader from '../components/LoadingHeader';
import CardImage from '../components/CardImage';
import { Helmet } from 'react-helmet-async';
import { useCurrencyStore } from '../lib/currencyStore';
import { formatPrice } from '../utils/formatPrice';
import { getCardPrice } from '../utils/cardPricing';
import { EnergyIcon } from '../components/icons/EnergyIcon';
import { getOptimalImageUrl } from '../utils/formatImageUrls';

export default function CardPage() {
  const { series, setName, cardName } = useParams();
  const [card, setCard] = useState<any | null>(null);
  const [similarCards, setSimilarCards] = useState<any[]>([]);
  const [setCards, setSetCards] = useState<any[]>([]);
  const [currentCardIndex, setCurrentCardIndex] = useState<number>(-1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingError, setLoadingError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { currency } = useCurrencyStore();
  const previousCardRef = useRef<{
    setName: string | null;
    cardName: string | null;
  }>({ setName: null, cardName: null });

  // "More from this Set" carousel state
  const setScrollRef = useRef<HTMLDivElement>(null);
  const [showSetLeftArrow, setShowSetLeftArrow] = useState(false);
  const [showSetRightArrow, setShowSetRightArrow] = useState(true);
  const setIsDragging = useRef(false);
  const setHasDragged = useRef(false);
  const setDragStartX = useRef(0);
  const setDragScrollLeft = useRef(0);

  // Check if we came from the search page
  const isFromSearch = location.state?.fromSearch;
  const searchQuery = localStorage.getItem('pokemonTcgSearchHistory');

  useEffect(() => {
    const fetchCardData = async () => {
      if (!setName || !cardName) return;
      
      // Check if we're loading a different card
      if (
        previousCardRef.current.setName === setName && 
        previousCardRef.current.cardName === cardName
      ) {
        return;
      }

      // Update the previous card reference
      previousCardRef.current = { setName, cardName };
      
      // Reset state for new card
      setCard(null);
      setSimilarCards([]);
      setSetCards([]);
      setCurrentCardIndex(-1);
      setLoading(true);
      setError(null);
      setLoadingError(null);
      
      try {
        // Extract card number from the URL
        const parts = cardName.split('-');
        const cardNumber = parts.pop()?.trim() || '';
        const cardNameWithoutNumber = parts.join(' ');
        const decodedSetName = decodeURIComponent(setName).replace(/-/g, ' ');
        const decodedSeries = series ? decodeURIComponent(series).replace(/-/g, ' ') : '';
        
        console.log('Fetching card data:', {
          cardName: cardNameWithoutNumber,
          cardNumber,
          setName: decodedSetName,
          series: decodedSeries
        });
        
        // Handle trainer gallery cards
        const isTrainerGallery = decodedSetName.toLowerCase().includes('trainer gallery');
        let setId = normalizeSetName(decodedSetName);
        
        if (isTrainerGallery) {
          // Extract base set name and append TG suffix
          const baseSetName = decodedSetName.replace(/trainer gallery/i, '').trim();
          setId = `${normalizeSetName(baseSetName)}tg`;
        }

        if (!setId) {
          throw new Error(`Could not normalize set name: ${decodedSetName}`);
        }

        // Construct the card ID using the set ID and card number
        const cardId = `${setId}-${cardNumber}`;
        console.log('Looking up card by ID:', cardId);
        
        // Direct database query by ID
        const { data: initialCardData, error: cardError } = await supabase
          .from('pokemontcg_io')
          .select(`
            *,
            set:pokemontcg_io_sets(*)
          `)
          .eq('id', cardId)
          .maybeSingle();

        if (cardError) {
          console.error('Error fetching card:', cardError);
          throw cardError;
        }

        let finalCardData = initialCardData;

        if (!finalCardData) {
          console.log('Card not found by ID, falling back to search');
          
          // Try searching by name and number within the set
          const { data: searchData, error: searchError } = await supabase
            .from('pokemontcg_io')
            .select(`
              *,
              set:pokemontcg_io_sets(*)
            `)
            .eq('set_id', setId)
            .eq('number', cardNumber)
            .ilike('name', `%${cardNameWithoutNumber}%`)
            .maybeSingle();
        
          if (searchError || !searchData) {
            throw new Error('Card not found');
          }

          finalCardData = searchData;
        }

        // Transform the card data
        const transformedCard = {
          ...finalCardData,
          flavor: finalCardData.flavor_text, // Use flavor_text from database
          images: {
            small: finalCardData.image_small_url || `https://yjzlookljahphgxcwpnv.supabase.co/storage/v1/object/public/pokemon-images-webp/pokemon_cards/${finalCardData.id}_small.webp`,
            large: finalCardData.image_large_url || `https://yjzlookljahphgxcwpnv.supabase.co/storage/v1/object/public/pokemon-images-webp/pokemon_cards/${finalCardData.id}_large.webp`
          },
          set: {
            ...finalCardData.set,
            releaseDate: finalCardData.set.release_date,
            images: {
              symbol: finalCardData.set.symbol_url,
              logo: finalCardData.set.logo_url
            }
          }
        };

        setCard(transformedCard);
        
        // Get all cards from the set for navigation
        try {
          const allSetCards = await getSetCards(setId);
          if (allSetCards && allSetCards.length > 0) {
            setSetCards(allSetCards);
            
            // Find current card index
            const cardIndex = allSetCards.findIndex(c => c.number === cardNumber);
            setCurrentCardIndex(cardIndex);
          }
        } catch (setCardsError) {
          console.error('Error fetching set cards for navigation:', setCardsError);
        }
        
        // Fetch similar cards
        try {
          const similarCardsResults = await getSimilarCards(transformedCard);
          setSimilarCards(similarCardsResults);
        } catch (similarError) {
          console.error('Error fetching similar cards:', similarError);
        }
      } catch (error) {
        console.error('Error fetching card:', error);
        setError('Card not found');
      } finally {
        setLoading(false);
      }
    };

    fetchCardData();
  }, [setName, cardName, series, navigate]);

  // "More from this Set" carousel scroll listener
  useEffect(() => {
    const el = setScrollRef.current;
    if (el) {
      const onScroll = () => {
        const { scrollLeft, scrollWidth, clientWidth } = el;
        setShowSetLeftArrow(scrollLeft > 0);
        setShowSetRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
      };
      onScroll();
      el.addEventListener('scroll', onScroll);
      return () => el.removeEventListener('scroll', onScroll);
    }
  }, [setCards]);

  // Cleanup drag state on unmount
  useEffect(() => {
    return () => {
      document.body.classList.remove('no-select');
    };
  }, []);

  // Preload "More from this Set" card images
  useEffect(() => {
    if (!setCards.length) return;
    setCards.forEach(c => {
      const url = c.images?.small
        || `https://yjzlookljahphgxcwpnv.supabase.co/storage/v1/object/public/pokemon-images-webp/pokemon_cards/${c.id}_small.webp`;
      const optimized = getOptimalImageUrl(url, { width: 200, quality: 75 });
      if (optimized) {
        const img = new Image();
        img.src = optimized;
      }
    });
  }, [setCards]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white">
        <header className="bg-white/5 border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 py-3">
            <div className="mb-4">
              <Breadcrumbs />
            </div>
            <LoadingHeader />
          </div>
        </header>
        <main className="max-w-7xl mx-auto px-4 py-8">
          <article className="bg-white/5 border border-white/10 rounded-lg">
            <CardDetailsSkeleton />
          </article>
        </main>
      </div>
    );
  }

  if (loadingError || error || !card) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-black text-white">
        <h1 className="text-2xl font-bold mb-4">Card Not Found</h1>
        <p className="text-white/60 mb-4">
          {loadingError || error || "We couldn't find the card you're looking for. This might be because:"}
        </p>
        {!loadingError && !error && (
          <ul className="list-disc text-white/60 mb-6 pl-6">
            <li>The URL might be incorrect</li>
            <li>The card number might be wrong</li>
            <li>The set name might not match exactly</li>
          </ul>
        )}
        <Link to="/" className="text-white/60 hover:text-white flex items-center gap-2">
          <ArrowLeft size={20} />
          Back to Home
        </Link>
      </div>
    );
  }

  const handleBack = () => {
    if (isFromSearch && searchQuery) {
      navigate('/', { state: { restoreSearch: true } });
    } else {
      navigate(`/${slugify(card.set.series)}/${slugify(card.set.name)}/`);
    }
  };

  const navigateToCard = (targetCard: any) => {
    const seriesSlug = slugify(targetCard.set.series);
    const setSlug = slugify(targetCard.set.name);
    const cardSlug = `${slugify(targetCard.name)}-${targetCard.number}`;
    navigate(`/${seriesSlug}/${setSlug}/${cardSlug}/`);
  };

  const handlePreviousCard = () => {
    if (currentCardIndex > 0 && setCards.length > 0) {
      const prevCard = setCards[currentCardIndex - 1];
      navigateToCard(prevCard);
    }
  };

  const handleNextCard = () => {
    if (currentCardIndex < setCards.length - 1 && setCards.length > 0) {
      const nextCard = setCards[currentCardIndex + 1];
      navigateToCard(nextCard);
    }
  };

  // Get random cards from the same set for "More from this Set"
  const getMoreFromThisSet = () => {
    if (!setCards.length || !card) return [];
    
    return setCards
      .filter(c => c.id !== card.id) // Exclude current card
      .sort(() => Math.random() - 0.5) // Randomize
      .slice(0, 8); // Show 8 cards
  };

  // "More from this Set" carousel helpers
  const moreFromSet = getMoreFromThisSet();

  const getSetCardsPerView = () => {
    if (typeof window !== 'undefined') {
      if (window.innerWidth < 640) return 2;
      if (window.innerWidth < 1024) return 3;
      return 5;
    }
    return 5;
  };

  const setTotalPages = Math.ceil(moreFromSet.length / getSetCardsPerView());

  const scrollSetPrev = () => {
    if (!setScrollRef.current) return;
    setScrollRef.current.scrollBy({ left: -300, behavior: 'smooth' });
  };

  const scrollSetNext = () => {
    if (!setScrollRef.current) return;
    setScrollRef.current.scrollBy({ left: 300, behavior: 'smooth' });
  };

  const handleSetDragStart = (e: React.PointerEvent) => {
    if (!setScrollRef.current) return;
    e.preventDefault();
    document.body.classList.add('no-select');
    setIsDragging.current = true;
    setHasDragged.current = false;
    setDragStartX.current = e.clientX;
    setDragScrollLeft.current = setScrollRef.current.scrollLeft;
  };

  const handleSetDragEnd = () => {
    document.body.classList.remove('no-select');
    setIsDragging.current = false;
  };

  const handleSetDragMove = (e: React.PointerEvent) => {
    if (!setIsDragging.current || !setScrollRef.current) return;
    e.preventDefault();
    const dx = e.clientX - setDragStartX.current;
    if (Math.abs(dx) > 5) {
      setHasDragged.current = true;
    }
    setScrollRef.current.scrollLeft = setDragScrollLeft.current - dx * 1.5;
  };

  const handleSetCardClick = (setCard: any, e: React.MouseEvent) => {
    if (setHasDragged.current) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    navigateToCard(setCard);
  };

  // Generate dynamic meta tags
  const cardTitle = card ? `${card.name} (#${card.number}) - ${card.set.name} - PokeCardIndex` : 'Loading Card...';
  const cardDescription = card ? 
    `${card.name} (#${card.number}) from ${card.set.name} set. ${card.types ? card.types.join('/') + ' type' : ''} ${card.hp ? `with ${card.hp} HP` : ''}. ${card.rarity || ''} card from the ${card.set.series} series. View detailed information, prices, and similar cards.` :
    'Loading Pokémon card details...';
  const cardImage = card?.images?.large || card?.images?.small || 'https://pokecardindex.com/favicon.ico';
  const canonicalUrl = card ? `https://pokecardindex.com/${slugify(card.set.series)}/${slugify(card.set.name)}/${slugify(card.name)}-${card.number}` : '';

  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>{cardTitle}</title>
        <meta name="description" content={cardDescription} />
        <meta name="keywords" content={`${card?.name || ''}, pokemon card, ${card?.set?.name || ''}, ${card?.set?.series || ''}, card details, pokemon tcg`} />
        <meta property="og:title" content={cardTitle} />
        <meta property="og:description" content={cardDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={cardImage} />
        {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      </Helmet>
      <header className="bg-white/5 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="mb-4">
            <Breadcrumbs />
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={handleBack}
              className="text-white/60 hover:text-white transition-colors flex items-center gap-2"
            >
              <ArrowLeft size={20} />
              {isFromSearch && searchQuery ? 'Back to Search' : `Back to ${card.set.name}`}
            </button>
            <h1 className="text-xl font-bold">{card.name}</h1>
          </div>
          
          {/* Card Navigation */}
          {setCards.length > 0 && currentCardIndex >= 0 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
              <button
                onClick={handlePreviousCard}
                disabled={currentCardIndex === 0}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  currentCardIndex === 0 
                    ? 'text-white/30 cursor-not-allowed' 
                    : 'text-white/60 hover:text-white hover:bg-white/10'
                }`}
              >
                <ChevronLeft size={20} />
                <span className="hidden sm:inline">Previous Card</span>
              </button>
              
              <div className="text-center">
                <div className="text-white/60 text-sm">
                  Card {currentCardIndex + 1} of {setCards.length}
                </div>
                <div className="text-white text-sm font-medium">
                  {card.number}/{card.set.total}
                </div>
              </div>
              
              <button
                onClick={handleNextCard}
                disabled={currentCardIndex === setCards.length - 1}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  currentCardIndex === setCards.length - 1 
                    ? 'text-white/30 cursor-not-allowed' 
                    : 'text-white/60 hover:text-white hover:bg-white/10'
                }`}
              >
                <span className="hidden sm:inline">Next Card</span>
                <ChevronRight size={20} />
              </button>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <article className="bg-white/5 border border-white/10 rounded-lg">
          <CardDetails card={card} />
        </article>

        {similarCards.length > 0 && (
          <SimilarCards cards={similarCards} currentCardId={card.id} />
        )}

        {/* More from this Set */}
        {moreFromSet.length > 0 && (
          <div className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">More from {card.set.name}</h2>
              <Link
                to={`/${slugify(card.set.series)}/${slugify(card.set.name)}/`}
                className="text-white/60 hover:text-white transition-colors text-sm flex items-center gap-1"
              >
                View all cards
                <ChevronRight size={16} />
              </Link>
            </div>
            <div className="relative">
              {showSetLeftArrow && (
                <button
                  onClick={scrollSetPrev}
                  className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 p-2 bg-black/80 rounded-full text-white/60 hover:text-white transition-colors"
                  aria-label="Scroll left"
                >
                  <ChevronLeft size={24} />
                </button>
              )}

              {showSetRightArrow && (
                <button
                  onClick={scrollSetNext}
                  className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 p-2 bg-black/80 rounded-full text-white/60 hover:text-white transition-colors"
                  aria-label="Scroll right"
                >
                  <ChevronRight size={24} />
                </button>
              )}

              <div
                ref={setScrollRef}
                className="flex overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-2 px-4 select-none"
                onPointerDown={handleSetDragStart}
                onPointerUp={handleSetDragEnd}
                onPointerLeave={handleSetDragEnd}
                onPointerMove={handleSetDragMove}
                style={{
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                  WebkitUserSelect: 'none',
                  MozUserSelect: 'none',
                  userSelect: 'none',
                  touchAction: 'pan-y',
                  cursor: 'grab'
                }}
              >
                {moreFromSet.map((setCard) => (
                  <div
                    key={setCard.id}
                    className="flex-none w-[calc(50%-8px)] sm:w-[calc(33.333%-16px)] lg:w-[calc(20%-16px)] mr-4 snap-start"
                  >
                    <div
                      className="group relative bg-white/5 border border-white/10 rounded-lg overflow-hidden cursor-pointer hover:ring-2 hover:ring-white/25 transition-all h-full"
                      onClick={(e) => handleSetCardClick(setCard, e)}
                    >
                      <div className="relative aspect-[2.5/3.5]">
                        <CardImage
                          src={setCard.images?.small || `https://yjzlookljahphgxcwpnv.supabase.co/storage/v1/object/public/pokemon-images-webp/pokemon_cards/${setCard.id}_small.webp`}
                          alt={setCard.name}
                          cardId={setCard.id}
                          className="absolute inset-0 w-full h-full object-cover"
                          width={200}
                          quality={75}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-lg">
                          <div className="absolute bottom-0 inset-x-0 p-3">
                            <div className="flex items-center justify-between mb-1">
                              <div>
                                <p className="text-sm font-medium text-white truncate">{setCard.name}</p>
                                <p className="text-xs text-white/60">
                                  {setCard.number}/{card.set.total}
                                </p>
                              </div>
                              {setCard.types?.[0] && (
                                <EnergyIcon type={setCard.types[0]} />
                              )}
                            </div>
                            {(() => {
                              const price = getCardPrice(setCard);
                              return price ? (
                                <p className="text-sm font-medium text-white/80">
                                  {formatPrice(price, currency)}
                                </p>
                              ) : null;
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {setTotalPages > 1 && (
                <div className="flex justify-center mt-4 gap-2">
                  {Array.from({ length: Math.min(setTotalPages, 5) }).map((_, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        if (setScrollRef.current) {
                          const cardWidth = setScrollRef.current.clientWidth / getSetCardsPerView();
                          setScrollRef.current.scrollTo({
                            left: index * cardWidth * getSetCardsPerView(),
                            behavior: 'smooth'
                          });
                        }
                      }}
                      className="w-2 h-2 rounded-full transition-colors bg-white/20 hover:bg-white"
                      aria-label={`Go to page ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}